import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { vergleichApi, vertraegeApi, dokumenteApi } from '../api';

// ─── KONSTANTEN ───────────────────────────────────────────────────────────────

const DIR_CONFIG = {
  WORSENING:   { icon: '↓', label: 'Verschlechtert', color: '#ef4444', bg: '#450a0a' },
  IMPROVEMENT: { icon: '↑', label: 'Verbessert',     color: '#22c55e', bg: '#052e16' },
  NEUTRAL:     { icon: '=', label: 'Neutral',         color: '#64748b', bg: '#1e293b' },
  UNKNOWN:     { icon: '?', label: 'Unklar',          color: '#eab308', bg: '#422006' },
};

const SEV_CONFIG = {
  critical: { label: 'Kritisch', color: '#ef4444', bg: '#450a0a' },
  high:     { label: 'Hoch',     color: '#f97316', bg: '#431407' },
  medium:   { label: 'Mittel',   color: '#eab308', bg: '#422006' },
  low:      { label: 'Niedrig',  color: '#22c55e', bg: '#052e16' },
  info:     { label: 'Info',     color: '#64748b', bg: '#1e293b' },
};

const CAT_LABELS = {
  COST:            '💶 Kosten',
  DEADLINE:        '⏱ Fristen',
  TERM:            '📅 Laufzeit',
  AUTO_RENEWAL:    '🔄 Auto-Verlängerung',
  PROVIDER:        '🏢 Anbieter',
  CONTRACT_NUMBER: '🔢 Vertragsnummer',
  RISK:            '⚠️ Risiken',
  CLAUSE:          '📜 Klauseln',
  DOCUMENT:        '📄 Dokumente',
  DATA_QUALITY:    '📝 Datenpflege',
  OTHER:           '🔹 Sonstiges',
};

const FACTTYPE_CFG = {
  FAKT:      { color: '#22c55e' },
  INDIKATOR: { color: '#eab308' },
  SCHÄTZUNG: { color: '#64748b' },
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function euro(v) {
  if (v == null || isNaN(v)) return '–';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v);
}

// ─── CONTRACT PICKER ─────────────────────────────────────────────────────────

function ContractPicker({ label, value, onChange }) {
  const [contracts, setContracts] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    vertraegeApi.list().then(r => setContracts(r.data || [])).catch(() => {});
  }, []);

  const filtered = contracts.filter(c =>
    !query || c.titel?.toLowerCase().includes(query.toLowerCase()) ||
    c.anbieter?.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 20);

  const selected = value ? contracts.find(c => c.id === value) : null;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{label}</div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', padding: '10px 14px', background: '#1e293b',
          border: `1px solid ${selected ? '#6366f1' : '#334155'}`,
          borderRadius: 8, color: selected ? '#f1f5f9' : '#64748b',
          textAlign: 'left', cursor: 'pointer', fontSize: 13,
        }}
      >
        {selected ? `${selected.titel}${selected.anbieter ? ` – ${selected.anbieter}` : ''}` : 'Vertrag auswählen…'}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: '#1e293b', border: '1px solid #334155', borderRadius: 8,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)', maxHeight: 320, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <input
            autoFocus
            placeholder="Suchen…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{ padding: '8px 12px', background: '#0f172a', border: 'none', borderBottom: '1px solid #334155', color: '#f1f5f9', fontSize: 13, outline: 'none' }}
          />
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 && <div style={{ padding: '12px 14px', color: '#64748b', fontSize: 13 }}>Keine Verträge gefunden</div>}
            {filtered.map(c => (
              <button
                key={c.id}
                onClick={() => { onChange(c.id); setOpen(false); setQuery(''); }}
                style={{
                  display: 'block', width: '100%', padding: '9px 14px',
                  background: c.id === value ? '#1e3a5f' : 'transparent',
                  border: 'none', borderBottom: '1px solid #0f172a',
                  color: '#f1f5f9', textAlign: 'left', cursor: 'pointer', fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 600 }}>{c.titel}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>
                  {c.anbieter || ''}{c.kosten ? ` · ${euro(c.kosten)}` : ''}{c.zahlungsintervall ? `/${c.zahlungsintervall === 'monatlich' ? 'Mo.' : c.zahlungsintervall}` : ''}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DOCUMENT PICKER ──────────────────────────────────────────────────────────

function DocumentPicker({ label, value, onChange }) {
  const [docs, setDocs] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    dokumenteApi.list({ limit: 200 }).then(r => setDocs(Array.isArray(r.data) ? r.data : (r.data?.dokumente || []))).catch(() => {});
  }, []);

  const filtered = docs.filter(d =>
    !query || (d.originaldateiname || d.dateiname || '').toLowerCase().includes(query.toLowerCase())
  ).slice(0, 20);

  const selected = value ? docs.find(d => d.id === value) : null;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{label}</div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', padding: '10px 14px', background: '#1e293b',
          border: `1px solid ${selected ? '#6366f1' : '#334155'}`,
          borderRadius: 8, color: selected ? '#f1f5f9' : '#64748b',
          textAlign: 'left', cursor: 'pointer', fontSize: 13,
        }}
      >
        {selected ? (selected.originaldateiname || selected.dateiname) : 'Dokument auswählen…'}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: '#1e293b', border: '1px solid #334155', borderRadius: 8,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)', maxHeight: 320, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <input
            autoFocus
            placeholder="Suchen…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{ padding: '8px 12px', background: '#0f172a', border: 'none', borderBottom: '1px solid #334155', color: '#f1f5f9', fontSize: 13, outline: 'none' }}
          />
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 && <div style={{ padding: '12px 14px', color: '#64748b', fontSize: 13 }}>Keine Dokumente gefunden</div>}
            {filtered.map(d => (
              <button
                key={d.id}
                onClick={() => { onChange(d.id); setOpen(false); setQuery(''); }}
                style={{
                  display: 'block', width: '100%', padding: '9px 14px',
                  background: d.id === value ? '#1e3a5f' : 'transparent',
                  border: 'none', borderBottom: '1px solid #0f172a',
                  color: '#f1f5f9', textAlign: 'left', cursor: 'pointer', fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 600 }}>{d.originaldateiname || d.dateiname}</div>
                {d.risiko_stufe && <div style={{ fontSize: 11, color: '#64748b' }}>Risiko: {d.risiko_stufe}</div>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SUMMARY CARD ─────────────────────────────────────────────────────────────

function SummaryCard({ summary, result, navigate }) {
  const overall = DIR_CONFIG[summary.overallDirection] || DIR_CONFIG.NEUTRAL;
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        {/* Gesamtbewertung */}
        <div style={{
          background: overall.bg, border: `2px solid ${overall.color}`,
          borderRadius: 10, padding: '12px 16px', textAlign: 'center', minWidth: 120,
        }}>
          <div style={{ fontSize: 32, fontWeight: 900, color: overall.color }}>{overall.icon}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: overall.color }}>{overall.label}</div>
          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>Impact: {summary.impactScore}/100</div>
        </div>

        {/* Zahlen */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 }}>
            {summary.leftLabel} <span style={{ color: '#64748b' }}>vs</span> {summary.rightLabel}
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
            <span style={{ background: '#450a0a', color: '#fca5a5', border: '1px solid #991b1b', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
              ↓ {summary.worsenings} Verschl.
            </span>
            <span style={{ background: '#052e16', color: '#86efac', border: '1px solid #15803d', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
              ↑ {summary.improvements} Verb.
            </span>
            <span style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>
              = {summary.neutral} Neutral
            </span>
          </div>
          <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.5 }}>{summary.recommendation}</div>
        </div>
      </div>

      {/* Top Findings */}
      {(summary.topWorsenings?.length > 0 || summary.topImprovements?.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
          {summary.topWorsenings?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Verschlechterungen</div>
              {summary.topWorsenings.map((w, i) => <div key={i} style={{ fontSize: 12, color: '#94a3b8', padding: '2px 0' }}>↓ {w}</div>)}
            </div>
          )}
          {summary.topImprovements?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#22c55e', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Verbesserungen</div>
              {summary.topImprovements.map((w, i) => <div key={i} style={{ fontSize: 12, color: '#94a3b8', padding: '2px 0' }}>↑ {w}</div>)}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
        {result.left?.id && <button onClick={() => navigate(`/vertraege/${result.left.id}`)} style={actionBtn}>📋 Links öffnen</button>}
        {result.right?.id && <button onClick={() => navigate(`/vertraege/${result.right.id}`)} style={actionBtn}>📋 Rechts öffnen</button>}
        <button onClick={() => navigate('/radar')} style={actionBtn}>📡 Radar</button>
        <button onClick={() => navigate('/optimierung')} style={actionBtn}>💡 Optimierung</button>
      </div>
    </div>
  );
}

const actionBtn = {
  background: '#1e3a5f', border: '1px solid #2563eb', color: '#93c5fd',
  borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
};

// ─── DIFFERENCE CARD ──────────────────────────────────────────────────────────

function DiffCard({ change }) {
  const dir = DIR_CONFIG[change.direction] || DIR_CONFIG.NEUTRAL;
  const sev = SEV_CONFIG[change.severity] || SEV_CONFIG.info;
  const ft = FACTTYPE_CFG[change.factType] || FACTTYPE_CFG['FAKT'];

  return (
    <div style={{
      background: '#1e293b', borderRadius: 10,
      border: `1px solid ${sev.color}33`,
      borderLeft: `4px solid ${dir.color}`,
      padding: '12px 14px', marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div
          aria-label={dir.label}
          style={{
            width: 32, height: 32, borderRadius: 6, background: dir.bg, border: `1px solid ${dir.color}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, fontWeight: 900, color: dir.color, flexShrink: 0,
          }}
        >{dir.icon}</div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9' }}>{change.label}</span>
            <span style={{ fontSize: 10, color: '#64748b', background: '#0f172a', borderRadius: 4, padding: '1px 5px' }}>
              {CAT_LABELS[change.category] || change.category}
            </span>
            <span style={{ fontSize: 10, fontWeight: 700, color: dir.color, marginLeft: 'auto' }}>{dir.label}</span>
          </div>

          {/* Vergleich */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 6, alignItems: 'center', marginBottom: 8 }}>
            <div style={{ background: '#0f172a', borderRadius: 6, padding: '5px 8px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
              <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>LINKS</div>
              {change.leftDisplay || '–'}
            </div>
            <span style={{ color: '#334155', fontSize: 14 }}>→</span>
            <div style={{ background: '#0f172a', borderRadius: 6, padding: '5px 8px', fontSize: 12, color: dir.color === '#64748b' ? '#94a3b8' : dir.color, textAlign: 'center', fontWeight: change.direction !== 'NEUTRAL' ? 600 : 400 }}>
              <div style={{ fontSize: 9, color: '#64748b', marginBottom: 2 }}>RECHTS</div>
              {change.rightDisplay || '–'}
            </div>
          </div>

          <div style={{ fontSize: 12, color: '#94a3b8' }}>{change.description}</div>
          {change.impact && <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>{change.impact}</div>}
          <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: ft.color, background: '#0f172a', borderRadius: 3, padding: '1px 5px', border: `1px solid ${ft.color}33` }}>
              {change.factType}
            </span>
            {change.severity !== 'info' && (
              <span style={{ fontSize: 10, color: sev.color, background: sev.bg, borderRadius: 3, padding: '1px 5px' }}>{sev.label}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── COMPARISON HEADER ────────────────────────────────────────────────────────

function ComparisonHeader({ left, right, comparisonType }) {
  const isContract = comparisonType === 'contract';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 10, marginBottom: 16, alignItems: 'stretch' }}>
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '12px 14px' }}>
        <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Links</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>{isContract ? left.titel : (left.name || `Dok ${left.id}`)}</div>
        {isContract && <div style={{ fontSize: 12, color: '#94a3b8' }}>{left.anbieter || ''}</div>}
        {isContract && left.monthlyCost != null && <div style={{ fontSize: 13, fontWeight: 600, color: '#6366f1', marginTop: 4 }}>{euro(left.monthlyCost)}/Mo.</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: '#334155', fontWeight: 900, padding: '0 4px' }}>VS</div>
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '12px 14px' }}>
        <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Rechts</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>{isContract ? right.titel : (right.name || `Dok ${right.id}`)}</div>
        {isContract && <div style={{ fontSize: 12, color: '#94a3b8' }}>{right.anbieter || ''}</div>}
        {isContract && right.monthlyCost != null && <div style={{ fontSize: 13, fontWeight: 600, color: '#6366f1', marginTop: 4 }}>{euro(right.monthlyCost)}/Mo.</div>}
      </div>
    </div>
  );
}

// ─── DIFF SECTION ─────────────────────────────────────────────────────────────

function DiffSection({ differences, textSnippets }) {
  const [activeCategory, setActiveCategory] = useState('');

  const categories = [...new Set(differences.map(c => c.category))];
  const filtered = activeCategory ? differences.filter(c => c.category === activeCategory) : differences;

  return (
    <div>
      {/* Filter */}
      {categories.length > 1 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          <button onClick={() => setActiveCategory('')} style={catBtn(activeCategory === '')}>Alle ({differences.length})</button>
          {categories.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)} style={catBtn(activeCategory === cat)}>
              {(CAT_LABELS[cat] || cat)} ({differences.filter(c => c.category === cat).length})
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
          <div style={{ color: '#94a3b8' }}>Keine Unterschiede in dieser Kategorie.</div>
        </div>
      )}

      {filtered.map(c => <DiffCard key={c.id} change={c} />)}

      {/* OCR Text Snippets */}
      {textSnippets && textSnippets.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, borderBottom: '1px solid #334155', paddingBottom: 6 }}>
            🔍 Relevante Textänderungen (Snippets)
          </div>
          {textSnippets.map((s, i) => (
            <div key={i} style={{
              background: s.type === 'added' ? '#052e16' : '#450a0a',
              border: `1px solid ${s.type === 'added' ? '#15803d' : '#991b1b'}`,
              borderRadius: 8, padding: '8px 12px', marginBottom: 6, fontSize: 12, color: '#e2e8f0',
            }}>
              <span style={{ fontWeight: 700, color: s.type === 'added' ? '#22c55e' : '#ef4444', marginRight: 6 }}>
                {s.type === 'added' ? '+ Neu:' : '− Entfernt:'}
              </span>
              {s.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const catBtn = (active) => ({
  padding: '4px 10px', borderRadius: 6, border: '1px solid',
  cursor: 'pointer', fontSize: 11, fontWeight: active ? 700 : 400,
  background: active ? '#4f46e5' : '#1e293b',
  borderColor: active ? '#4f46e5' : '#334155',
  color: active ? '#fff' : '#94a3b8',
});

// ─── HAUPTSEITE ───────────────────────────────────────────────────────────────

export default function VergleichPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [compType, setCompType] = useState('vertraege');
  const [leftId, setLeftId] = useState(searchParams.get('leftContract') ? Number(searchParams.get('leftContract')) : null);
  const [rightId, setRightId] = useState(null);
  const [leftDocId, setLeftDocId] = useState(searchParams.get('leftDoc') ? Number(searchParams.get('leftDoc')) : null);
  const [rightDocId, setRightDocId] = useState(null);

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canCompare = compType === 'vertraege' ? (leftId && rightId) : (leftDocId && rightDocId);

  const runCompare = useCallback(() => {
    setLoading(true);
    setError(null);
    setResult(null);
    const req = compType === 'vertraege'
      ? vergleichApi.vertraege(leftId, rightId)
      : vergleichApi.dokumente(leftDocId, rightDocId);
    req
      .then(d => setResult(d))
      .catch(e => setError(e?.response?.data?.error || 'Vergleich fehlgeschlagen.'))
      .finally(() => setLoading(false));
  }, [compType, leftId, rightId, leftDocId, rightDocId]);

  // Auto-compare when both selected
  useEffect(() => {
    if (canCompare) runCompare();
  }, [canCompare, runCompare]);

  return (
    <div style={{ padding: '16px 16px 48px', maxWidth: 860, margin: '0 auto' }}>
      {/* Titel */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <span style={{ fontSize: 28 }}>⚖️</span>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#f1f5f9' }}>Vergleich</h1>
          <div style={{ fontSize: 12, color: '#64748b' }}>Verträge · Dokumente · Änderungen</div>
        </div>
      </div>

      {/* Typ-Wahl */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['vertraege', '📋 Verträge'], ['dokumente', '📄 Dokumente']].map(([v, l]) => (
          <button
            key={v}
            onClick={() => { setCompType(v); setResult(null); setError(null); }}
            style={{
              padding: '8px 18px', borderRadius: 8, border: '1px solid',
              cursor: 'pointer', fontSize: 14, fontWeight: 700,
              background: compType === v ? '#4f46e5' : '#1e293b',
              borderColor: compType === v ? '#4f46e5' : '#334155',
              color: compType === v ? '#fff' : '#94a3b8',
            }}
          >{l}</button>
        ))}
      </div>

      {/* Picker */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'end', marginBottom: 20 }}>
        {compType === 'vertraege' ? (
          <>
            <ContractPicker label="Vertrag A (Links)" value={leftId} onChange={setLeftId} />
            <div style={{ color: '#334155', fontWeight: 900, fontSize: 20, paddingBottom: 10 }}>VS</div>
            <ContractPicker label="Vertrag B (Rechts)" value={rightId} onChange={setRightId} />
          </>
        ) : (
          <>
            <DocumentPicker label="Dokument A (Links)" value={leftDocId} onChange={setLeftDocId} />
            <div style={{ color: '#334155', fontWeight: 900, fontSize: 20, paddingBottom: 10 }}>VS</div>
            <DocumentPicker label="Dokument B (Rechts)" value={rightDocId} onChange={setRightDocId} />
          </>
        )}
      </div>

      {/* Vergleich starten */}
      {canCompare && !loading && (
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <button
            onClick={runCompare}
            style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
          >
            ↻ Vergleich neu laden
          </button>
        </div>
      )}

      {/* Laden */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
          ⚖️ Vergleich wird berechnet…
        </div>
      )}

      {/* Fehler */}
      {error && (
        <div style={{ background: '#450a0a', border: '1px solid #991b1b', color: '#fca5a5', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Ergebnis */}
      {result && !loading && (
        <>
          <ComparisonHeader left={result.left} right={result.right} comparisonType={result.comparisonType} />
          <SummaryCard summary={result.summary} result={result} navigate={navigate} />

          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, borderBottom: '1px solid #334155', paddingBottom: 6 }}>
            Detaillierte Unterschiede · {result.differences?.length || 0} Änderungen
          </div>

          <DiffSection differences={result.differences || []} textSnippets={result.textSnippets} />

          {/* Noch keine Auswahl */}
          {result.differences?.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
              <div style={{ color: '#94a3b8', fontSize: 15 }}>Keine wesentlichen Unterschiede in den vergleichbaren Feldern gefunden.</div>
              {result.confidence < 0.7 && (
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                  Hinweis: Konfidenz {(result.confidence * 100).toFixed(0)} % – Dokumente möglicherweise nicht vollständig analysiert.
                </div>
              )}
            </div>
          )}

          {/* Confidence + Timestamp */}
          <div style={{ fontSize: 11, color: '#334155', textAlign: 'center', marginTop: 16 }}>
            Konfidenz: {((result.confidence || 0) * 100).toFixed(0)} % · Generiert: {new Date(result.generatedAt).toLocaleString('de-DE')}
          </div>
        </>
      )}

      {/* Leerzustand */}
      {!result && !loading && !error && (
        <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚖️</div>
          <div style={{ fontSize: 16, color: '#94a3b8', fontWeight: 600 }}>
            {compType === 'vertraege' ? 'Zwei Verträge auswählen und vergleichen' : 'Zwei Dokumente auswählen und vergleichen'}
          </div>
          <div style={{ fontSize: 13, marginTop: 8 }}>
            Der Vergleich analysiert Kosten, Fristen, Risiken, Klauseln und Dokumentenlage.
          </div>
        </div>
      )}
    </div>
  );
}
